import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Search,
  List,
  Smartphone,
  Sparkles,
  Settings,
  DollarSign,
  UtensilsCrossed,
  Eye,
} from 'lucide-react';
import { WizardStep } from '../components/FeatureWizard';

export const getModernOrderListWizardSteps = (
  translate: (key: string) => string
): WizardStep[] => {
  return [
    {
      id: 'welcome',
      title: translate('pos.wizards.modernOrderList.welcome.title'),
      description: translate(
        'pos.wizards.modernOrderList.welcome.description'
      ),
      content: (
        <div>
          <Alert className="mb-4">
            <AlertDescription>
              {translate('pos.wizards.modernOrderList.welcome.info')}
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground">
            {translate('pos.wizards.modernOrderList.welcome.content')}
          </p>
        </div>
      ),
    },
    {
      id: 'search',
      title: translate('pos.wizards.modernOrderList.search.title'),
      description: translate(
        'pos.wizards.modernOrderList.search.description'
      ),
      content: (
        <div>
          <Card className="mb-4 bg-primary/5 border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-primary text-sm">
                <Search className="h-4 w-4" />
                {translate(
                  'pos.wizards.modernOrderList.search.highlight'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {translate('pos.wizards.modernOrderList.search.details')}
              </p>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Search className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.search.feature1'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.search.feature1Desc'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.search.feature2'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.search.feature2Desc'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'responsive',
      title: translate('pos.wizards.modernOrderList.responsive.title'),
      description: translate(
        'pos.wizards.modernOrderList.responsive.description'
      ),
      content: (
        <div>
          <div className="flex gap-4 mb-4">
            <Card className="flex-1 text-center">
              <CardContent className="pt-6">
                <List className="h-10 w-10 mx-auto mb-2 text-primary" />
                <p className="font-medium text-sm mb-1">
                  {translate(
                    'pos.wizards.modernOrderList.responsive.desktop'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.responsive.desktopDesc'
                  )}
                </p>
              </CardContent>
            </Card>
            <Card className="flex-1 text-center">
              <CardContent className="pt-6">
                <Smartphone className="h-10 w-10 mx-auto mb-2 text-primary" />
                <p className="font-medium text-sm mb-1">
                  {translate(
                    'pos.wizards.modernOrderList.responsive.mobile'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.responsive.mobileDesc'
                  )}
                </p>
              </CardContent>
            </Card>
          </div>
          <Alert>
            <AlertDescription>
              {translate('pos.wizards.modernOrderList.responsive.tip')}
            </AlertDescription>
          </Alert>
        </div>
      ),
    },
    {
      id: 'bulkRelease',
      title: translate('pos.wizards.modernOrderList.bulkRelease.title'),
      description: translate(
        'pos.wizards.modernOrderList.bulkRelease.description'
      ),
      content: (
        <div>
          <Card className="mb-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm">
                <UtensilsCrossed className="h-4 w-4" />
                {translate(
                  'pos.wizards.modernOrderList.bulkRelease.highlight'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {translate(
                  'pos.wizards.modernOrderList.bulkRelease.details'
                )}
              </p>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <UtensilsCrossed className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.bulkRelease.feature1'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.bulkRelease.feature1Desc'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Eye className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.bulkRelease.feature2'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.bulkRelease.feature2Desc'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'productNames',
      title: translate('pos.wizards.modernOrderList.productNames.title'),
      description: translate(
        'pos.wizards.modernOrderList.productNames.description'
      ),
      content: (
        <div>
          <Card className="mb-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm">
                <Sparkles className="h-4 w-4" />
                {translate(
                  'pos.wizards.modernOrderList.productNames.highlight'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {translate(
                  'pos.wizards.modernOrderList.productNames.details'
                )}
              </p>
            </CardContent>
          </Card>
          <div className="flex gap-2 flex-wrap mb-4">
            <Badge variant="outline" className="text-primary border-primary">
              Product Name
            </Badge>
            <Badge variant="outline" className="text-secondary border-secondary">
              (REF-001)
            </Badge>
            <Badge variant="outline" className="text-green-600 border-green-600">
              ₫50,000
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {translate('pos.wizards.modernOrderList.productNames.tip')}
          </p>
        </div>
      ),
    },
    {
      id: 'currencySettings',
      title: translate(
        'pos.wizards.modernOrderList.currencySettings.title'
      ),
      description: translate(
        'pos.wizards.modernOrderList.currencySettings.description'
      ),
      content: (
        <div>
          <Card className="mb-4 bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 text-sm">
                <DollarSign className="h-4 w-4" />
                {translate(
                  'pos.wizards.modernOrderList.currencySettings.highlight'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {translate(
                  'pos.wizards.modernOrderList.currencySettings.details'
                )}
              </p>
            </CardContent>
          </Card>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.currencySettings.feature1'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.currencySettings.feature1Desc'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">
                  {translate(
                    'pos.wizards.modernOrderList.currencySettings.feature2'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.wizards.modernOrderList.currencySettings.feature2Desc'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      action: {
        label: translate(
          'pos.wizards.modernOrderList.currencySettings.action'
        ),
        onClick: () => {
          // Navigate to settings
          window.location.href = '/settings';
        },
      },
    },
    {
      id: 'versionSwitcher',
      title: translate(
        'pos.wizards.modernOrderList.versionSwitcher.title'
      ),
      description: translate(
        'pos.wizards.modernOrderList.versionSwitcher.description'
      ),
      content: (
        <div>
          <Card className="mb-4 bg-secondary/10 border-secondary/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-secondary text-sm">
                <Sparkles className="h-4 w-4" />
                {translate(
                  'pos.wizards.modernOrderList.versionSwitcher.highlight'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                {translate(
                  'pos.wizards.modernOrderList.versionSwitcher.details'
                )}
              </p>
            </CardContent>
          </Card>
          <div className="flex gap-2 flex-wrap mb-4">
            <Badge>Modern</Badge>
            <Badge variant="outline">Legacy</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {translate('pos.wizards.modernOrderList.versionSwitcher.tip')}
          </p>
        </div>
      ),
    },
  ];
};
